import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { MODAL_DATA, MODAL_REF, ModalRef } from '@services/components/ModalService/modal.ref';

import { CANCEL_ORDER_REASONS } from '@constants/orders.constants';

export interface CancelOrderModalData {
  orderCode: string;
}

/** Valor del radio "Otro"; nunca se manda al back, solo indica que se use customReason. */
const OTHER_REASON_OPTION = 'other';

@Component({
  selector: 'app-cancel-order-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './cancel-order-modal.component.html',
  styleUrl: './cancel-order-modal.component.css',
})
export class CancelOrderModalComponent {
  private readonly modalRef = inject(MODAL_REF) as ModalRef<string>;
  private readonly data = inject<CancelOrderModalData>(MODAL_DATA);
  private readonly fb = inject(FormBuilder);

  protected readonly orderCode = this.data?.orderCode ?? '';
  protected readonly reasonOptions = CANCEL_ORDER_REASONS;
  protected readonly otherOptionValue = OTHER_REASON_OPTION;

  public readonly form = this.fb.nonNullable.group({
    reasonOption: ['', Validators.required],
    customReason: [''],
  });

  protected get isOtherSelected(): boolean {
    return this.form.controls.reasonOption.value === OTHER_REASON_OPTION;
  }

  protected get reasonOptionInvalid(): boolean {
    const ctrl = this.form.controls.reasonOption;
    return ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  protected get customReasonInvalid(): boolean {
    if (!this.isOtherSelected) {
      return false;
    }
    return !this.form.controls.customReason.value.trim() && this.form.controls.customReason.dirty;
  }

  public onCancel(): void {
    this.modalRef.close({ reason: 'cancel' });
  }

  public onSubmit(): void {
    const { reasonOption, customReason } = this.form.getRawValue();
    const otherMissing = reasonOption === OTHER_REASON_OPTION && !customReason.trim();

    if (!reasonOption || otherMissing) {
      this.form.markAllAsTouched();
      return;
    }

    // El motivo elegido ya es una oración legible; en "Otro" se usa el texto escrito tal cual.
    const finalReason = reasonOption === OTHER_REASON_OPTION ? customReason.trim() : reasonOption;
    this.modalRef.close({ reason: 'confirm', data: finalReason });
  }
}
